/**
 * Copyright © Veeam Software Group GmbH.
 */

import * as React from 'react';
import { EActionResultStatus, VbrDeploymentLicenseSourceType } from '@veeam-vspc/models/web-controllers';

import type { BaseSuccessRequestResponse } from '@veeam-vspc/core';

import { core } from 'core/core-module';
import { PageWizard } from 'components/wizards/PageWizard';
import { useLang } from 'views/providers/LangProvider/hooks';
import {
    getAccountStep,
    getDeploymentScheduleStep,
    getDistributionStep,
    getInstallationParametersStep,
    getLicenseStep,
    getPulseLicenseStep,
    getSummaryStep,
    getTenantInformationStep,
} from './components';
import { VbrDeploySettingsWizardModel } from './entries';
import { VbrDeployType } from './enums';
import { useAppServices } from 'views/providers/AppProvider/hooks';

import type { VbrWizardProps } from './interfaces';

export const InstallAndUpdateVBRWizard: React.FC<VbrWizardProps> = (props) => {
    const lang = useLang();
    const appServices = useAppServices();
    const { notificationService } = appServices;
    const model = new VbrDeploySettingsWizardModel(props);
    const isVbrDeploy = props.vbrDeployType === VbrDeployType.Install;
    const isLicenseFromPulse = model.licenseSource === VbrDeploymentLicenseSourceType.Pulse;
    const title = isVbrDeploy ? lang.INSTALL_BACKUP_SERVER : lang.UPGRADE_BACKUP_SERVER;
    const deploymentStepTitle = lang.SCHEDULE;

    if (!model.isPulseConfigured && isLicenseFromPulse) {
        model.licenseSource = VbrDeploymentLicenseSourceType.LicenseFile;
    }

    return (
        <PageWizard
            title={`${title} - ${props.hostName}`}
            deactivate={() => ({})}
            isEdit={false}
            data={model}
            steps={[
                getDistributionStep(lang),
                getAccountStep(lang),
                getInstallationParametersStep(lang),
                getLicenseStep(lang),
                getPulseLicenseStep(lang.VCSP_PULSE),
                getTenantInformationStep(lang.COMPANY_INFO, lang, appServices),
                getDeploymentScheduleStep(deploymentStepTitle),
                getSummaryStep(lang.SUMMARY),
            ]}
            onFinish={async({ data }) => {
                const url = isVbrDeploy ? 'DiscoveryRule/DeployBackupServer' : 'BackupServer/Upgrade';

                return core.transportService.request(url, {
                    ...props.vbrLicenseParams,
                    deploySettings: await data.serialize(),
                })
                    .then(resp => notificationService.multi(title, resp as BaseSuccessRequestResponse)
                        .then((res) => {
                            if (res.status !== EActionResultStatus.Error || !isVbrDeploy) {
                                props.onFinish();
                            } else {
                                return Promise.reject();
                            }
                        })
                    );
            }}
        />
    );
};
